import { replaceSearchString } from 'actions/NavigationActions'
import { ensureActiveChat, fetchChats } from 'actions/supportAssistant/supportAssistantActions'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { sortBy } from 'lib/array/arrayUtils'
import { haveOrdersBeenFetched } from 'lib/order/orderUtils'
import { useEffect, useMemo } from 'react'
import type { IntlShape } from 'react-intl'
import { useIntl } from 'react-intl'
import { useLocation } from 'react-router-dom'
import { getUpcomingOrders } from 'selectors/orderSelectors'
import { selectLoggedIn } from 'selectors/accountSelectors'

export function getDefaultChatTitle(intl: IntlShape): string {
  return intl.formatMessage({
    id: '3cRDyDGuWN2rdBUx4rGT',
    defaultMessage: 'New chat',
    description: 'Default title for a new support assistant chat in the customer support assistant',
  })
}

export function getDefaultChat(intl: IntlShape): App.Chat {
  return {
    id: 'default',
    title: getDefaultChatTitle(intl),
    status: 'ACTIVE',
    createdAt: new Date().toString(),
    updatedAt: new Date().toString(),
  }
}

export function getEmptyChat(intl: IntlShape): App.SupportAssistantChatsState {
  return {
    chats: [getDefaultChat(intl)],
    fetching: false,
    error: undefined,
  }
}

function useChats(): [Array<App.Chat>, boolean | undefined, any] {
  const dispatch = useAppDispatch()
  const intl = useIntl()
  const chats = useAppSelector(state => state.supportAssistant.chats)
  const ordersFetched = useAppSelector(state => haveOrdersBeenFetched(state, 'upcoming'))
  const upcoming = useAppSelector(state => getUpcomingOrders(state))
  const loggedIn = useAppSelector(selectLoggedIn)

  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const chatDeepLink = queryParams.get('list-orders') === 'true' ? 'list-orders' : 'none'
  const chatDeepLinkKey: App.ChatDeepLinks = chatDeepLink

  const newChatTitle = getDefaultChatTitle(intl)
  const fallbackChats = useMemo(() => getEmptyChat(intl), [intl])
  const realChats = chats && chats.chats?.length > 0 ? chats : fallbackChats

  const sortedRealChats = useMemo(() => {
    const sortedChats = sortBy(realChats.chats, chat => +(new Date(chat.updatedAt)), 'desc')

    return sortedChats.map(chat => chat.id === 'default' ? { ...chat, title: newChatTitle } : chat)
  }, [newChatTitle, realChats.chats])

  useEffect(() => {
    if (ordersFetched) {
      dispatch(fetchChats(upcoming, chatDeepLinkKey))
    }

    dispatch(replaceSearchString({ 'list-orders': undefined }))
  }, [chatDeepLinkKey, dispatch, loggedIn, ordersFetched, upcoming])

  useEffect(() => {
    if (loggedIn && !chats.fetching && !chats.error) {
      dispatch(ensureActiveChat())
    }
  }, [chats, loggedIn, dispatch])

  return [
    sortedRealChats,
    realChats.fetching,
    realChats.error,
  ]
}

export default useChats
